import {cx} from "emotion";
import * as React from "react";

import "./ride-along-image.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {IntervalWatch}  from "@Common/react/interval-watch";
import {keyed}          from "@Common/react/keyed";
import {Load2}          from "@Common/react/load2";
import {scnv}           from "@Common/react/state-change-nested-value";
import {Static2}        from "@Common/react/static-2";
import {UseState}       from "@Common/react/use-state";
import {cnv}            from "@Common/utils/arr-path";
import {chain}          from "@Common/utils/fs";

import {InformationBadge} from "../../../../../common/design2/information-badge/information-badge";
import {Radio}            from "../../../../../common/design2/radio/radio";

import {ImagesModal} from "./images-modal/images-modal";

export const RideAlongImage = ({initData, next}) => cs(
    consumeContext("apis"),
    ["images", ({apis}, next) => Load2({
        fetch: () => apis.next.informedDelivery.getRideAlong(),
        next,
    })],

    ["rideAlongImg", (_, next) => UseState({
        initValue: chain(
            initData?.rideAlongImage,
            (_) => ({
                saved: _,
                current: _,
            }),
        ),
        next,
    })],

    ["calImg", ({rideAlongImg, images}, next) => next(
        rideAlongImg.value.current || images.value?.[0]?.url
    )],

    ({images, rideAlongImg, calImg}) => next({
        editor: {
            valid: true,

            loading: images.loading,

            $name: "Informed Delivery > EmailProof > RideAlong Img",
            changed: rideAlongImg.value?.saved !== calImg,
            prepareSave: (data) => ({
                newValue: chain(
                    data,
                    (_) => cnv(_, ["rideAlongImage"], () => calImg),
                ),
                onSaved: () => scnv(rideAlongImg, ["saved"], () => calImg),
            }),
        },
        renderPreview: ({className, href}) => calImg && (
            <div className={cx("ride-along-image-7j3 preview", className)}>
                <a
                    href={href}
                    target="_blank"
                    onClick={(e) => {
                        if (!href) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                >
                    <img {...{
                        src: calImg,

                        "xd-qa-type": "image",
                        "xd-qa-name": "Ride Along Image Preview",
                    }}/>
                </a>
            </div>
        ),
        renderEditing: ({className = undefined,}) => (
            <div className={cx("ride-along-image-7j3 editing", className)}>

                <div className="header">
                    <div className="left">
                        <div {...{
                            className: "title",

                            "xd-qa-type": "label",
                            "xd-qa-name": "Ride Along Image",
                        }}>
                            Ride Along Image
                        </div>

                        {InformationBadge({
                            getTooltip: () => "A clickable call to action. Appears in color beneath the image of your mailpiece.",
                            height: 14,
                        })}
                    </div>

                    <div className="right">
                        {cs(
                            ({}, next) => !!images.value?.length && next(),
                            ["imagesModal", (_, next) => ImagesModal({
                                images: images.value,
                                current: calImg,
                                next,
                            })],
                            ({imagesModal}) => (
                                <a {...{
                                    onClick: () => imagesModal.show({
                                        onDone: (src) => scnv(rideAlongImg, ["current"], () => src),
                                    }),

                                    "xd-qa-type": "link",
                                    "xd-qa-name": "Ride Along Image See All",
                                }}>
                                    See All ({images.value?.length??"--"})
                                </a>
                            )
                        )}
                    </div>
                </div>

                <div className="images">

                    {images.value?.result === "failure" ? (
                        <div className="error">
                            ERROR: {images.value.errors?.msg ?? "Unknown error"}
                        </div>
                    ) : !images.value?.length ? (
                        <div className="no-images">
                            No images available
                        </div>
                    ) : cs(
                        ["ref", (_, next) => Static2({next})],
                        ["scrolling", ({ref}, next) => IntervalWatch({
                            getValue: () => {
                                const dom = ref.get();
                                if (!dom) {
                                    return false;
                                }
                                return dom.getBoundingClientRect().height < dom.scrollHeight - 2;
                            },
                            next,
                        })],
                        ({ref, scrolling}) => (
                            <div className={cx("scroller", {scrolling})} ref={ref.set}>
                                {grab(images.value, 5, !calImg ? 0 : images.value.findIndex(({url}) => url === calImg)).map(({value: {url: src}, index}) => cs(
                                    keyed(src),
                                    () => {
                                        const selected = calImg === src;
                                        return (
                                            <div {...{
                                                className: cx("image", {selected}),
                                                onClick: () => {
                                                    scnv(rideAlongImg, ["current"], () => src);
                                                },

                                                "xd-qa-type": "card",
                                                "xd-qa-name": "Ride Along Image #" + (index+1),
                                            }}>
                                                {Radio({
                                                    selected,
                                                    size: "small",
                                                    xdQaName: "Ride Along Image Radio",
                                                })}

                                                <div className="img">
                                                    <img {...{
                                                        src: src,

                                                        "xd-qa-type": "image",
                                                        "xd-qa-name": "Ride Along Image Thumbnail",
                                                    }}/>
                                                </div>
                                            </div>
                                        );
                                    },
                                ))}
                            </div>
                        ),
                    )}

                </div>

            </div>
        ),
    }),
);

const grab = (list, size, index) => {
    if (list.length - index <= list.length % size) {
        const startIndex = Math.max(0, list.length - size);
        return list.slice(startIndex).map((value, i) => ({index: startIndex + i, value}));
    }

    const startIndex = Math.floor(index / size) * size;
    return list.slice(startIndex, startIndex + size).map((value, i) => ({index: startIndex + i, value}));
};